import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import Logo from '@/components/Logo';
import Panier from '@/components/Panier';
import ProductCard from '@/components/ProductCard';
import {
  useCatalogue,
  useCatalogueLines,
  useDebounced,
  type Filters,
  type Product,
} from '@/lib/catalogue';
import { useAjouterAuPanier, useOrder, useSouvenirDuPanier } from '@/lib/orders';
import { cn } from '@/lib/utils';
import Confirmation from './Confirmation';

/**
 * Plan §5.4 — deux colonnes en iPad paysage : catalogue à gauche, panier à droite.
 * En portrait, le panier passe sous le catalogue plutôt que d'écraser les deux.
 */
export default function Commande() {
  const { id } = useParams();
  const orderId = Number(id);
  const navigate = useNavigate();
  const { data, isPending, isError, error } = useOrder(Number.isInteger(orderId) ? orderId : null);
  const order = data?.order;
  useSouvenirDuPanier(order);

  const [q, setQ] = useState('');
  const [lineCode, setLineCode] = useState<string | null>(null);
  const [revision, setRevision] = useState(false);

  const debounced = useDebounced(q);
  const filters: Filters = useMemo(
    () => ({ q: debounced, lineCode, featuredOnly: false, sort: 'pertinence' }),
    [debounced, lineCode],
  );
  const catalogue = useCatalogue(filters);
  const lignes = useCatalogueLines();
  const ajouter = useAjouterAuPanier(orderId);

  if (isPending) {
    return <p className="text-muted-foreground p-8 text-center">Chargement de la commande…</p>;
  }
  if (isError || !order) {
    return (
      <div className="space-y-3 p-8 text-center">
        <p className="text-destructive font-medium">{(error as Error)?.message ?? 'Commande introuvable'}</p>
        <Link to="/" className="border-input inline-flex h-12 items-center rounded-lg border px-5">
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  // Une commande déjà soumise s'ouvre directement sur sa confirmation.
  if (order.status !== 'DRAFT' || revision) {
    return <Confirmation order={order} onRetour={() => setRevision(false)} />;
  }

  const mesLignes = lignes.data?.lines ?? [];

  return (
    <div className="flex h-full min-h-full flex-col lg:h-screen lg:flex-row">
      <section className="flex min-w-0 flex-1 flex-col">
        <header className="bg-card space-y-3 border-b p-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="border-input flex h-12 items-center rounded-lg border px-4 font-medium">
              ← Accueil
            </Link>
            <Logo className="h-8" />
            <span className="text-muted-foreground ml-auto text-sm">
              Commande en cours · brouillon
            </span>
          </div>

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="N° de pièce ou description…"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="border-input bg-background focus:ring-ring h-16 w-full rounded-lg border px-4 text-lg focus:ring-2 focus:outline-none"
          />

          {mesLignes.length > 1 && (
            <div className="flex flex-wrap gap-2">
              <Chip active={lineCode === null} onClick={() => setLineCode(null)}>
                Toutes
              </Chip>
              {mesLignes.map((l) => (
                <Chip
                  key={l.id}
                  active={lineCode === l.code}
                  onClick={() => setLineCode(lineCode === l.code ? null : l.code)}
                >
                  {l.code}
                </Chip>
              ))}
            </div>
          )}
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <p className="text-muted-foreground text-sm" aria-live="polite">
            {catalogue.isPending ? 'Chargement…' : `${catalogue.total} produit(s)`}
          </p>

          {ajouter.isError && (
            <p className="text-destructive font-medium">{(ajouter.error as Error).message}</p>
          )}

          <div data-testid="grille-produits" className="grid gap-3 2xl:grid-cols-2">
            {catalogue.products.map((p: Product) => (
              <ProductCard
                key={p.id}
                product={p}
                onSelect={() => ajouter.mutate({ productId: p.id })}
              />
            ))}
          </div>

          {catalogue.hasNextPage && (
            <button
              onClick={() => void catalogue.fetchNextPage()}
              disabled={catalogue.isFetchingNextPage}
              className="border-input h-14 w-full rounded-lg border font-medium disabled:opacity-50"
            >
              {catalogue.isFetchingNextPage ? 'Chargement…' : 'Afficher plus'}
            </button>
          )}
        </div>
      </section>

      <section className="lg:w-[26rem] lg:shrink-0">
        <Panier order={order} onSoumettre={() => setRevision(true)} />
      </section>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'h-12 rounded-lg border px-4 font-medium',
        active ? 'border-primary bg-primary/10' : 'border-input',
      )}
    >
      {children}
    </button>
  );
}
